import { createFileRoute, Link } from "@tanstack/react-router";
import type { BlogArticleSummary } from "@/lib/blog-types";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

function apiUrl(path: string) {
	if (typeof window !== "undefined") return path;
	const baseUrl =
		import.meta.env.VITE_SITE_URL ||
		import.meta.env.VITE_BETTER_AUTH_URL ||
		"http://localhost:3000";
	return `${baseUrl}${path}`;
}

export const Route = createFileRoute("/blog/")({
	loader: async () => {
		if (import.meta.env.SSR) {
			const { listBlogArticles } = await import("@/lib/blog-server");
			const articles = await listBlogArticles();
			return { articles };
		}

		const response = await fetch(apiUrl("/api/blog"));
		if (!response.ok) {
			throw new Error("Failed to load blog articles");
		}

		return response.json() as Promise<{ articles: BlogArticleSummary[] }>;
	},
	component: BlogIndex,
	head: ({ matches }) => {
		const internalPath = "/blog";
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath });

		return {
			meta: [
				{ title: "AgentReacher | Blog | AI Social Media Automation" },
				{
					name: "description",
					content:
						"Learn about AI-powered social media automation, modern scheduling workflows, and how to automate your marketing with AI agents.",
				},
				{ property: "og:title", content: "AgentReacher | Blog" },
				{
					property: "og:description",
					content: "Guides and insights on AI-powered social media automation.",
				},
				{ property: "og:image", content: ogImageUrl("blog") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:title", content: "AgentReacher | Blog" },
				{
					name: "twitter:description",
					content: "Guides and insights on AI-powered social media automation.",
				},
				{ name: "twitter:image", content: ogImageUrl("blog") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath }),
		};
	},
});

function formatDate(value: string) {
	return new Intl.DateTimeFormat("en", {
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(new Date(value));
}

function BlogIndex() {
	const { articles } = Route.useLoaderData();

	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<h1 className="text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
					Blog
				</h1>
				<p className="text-base text-muted-foreground">
					Guides and insights on AI-powered social scheduling.
				</p>
			</div>

			{articles.length === 0 ? (
				<p className="text-base text-muted-foreground">
					New articles will appear here soon.
				</p>
			) : (
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
					{articles.map((post) => (
						<Link
							key={post.slug}
							to="/blog/$slug"
							params={{ slug: post.slug }}
							className="group flex flex-col overflow-hidden rounded-md shadow-border transition-shadow hover:shadow-card"
						>
							<div className="aspect-[16/9] w-full overflow-hidden bg-muted">
								{post.imageUrl ? (
									<img
										src={post.imageUrl}
										alt=""
										loading="lazy"
										className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
									/>
								) : null}
							</div>
							<div className="flex flex-1 flex-col gap-2 p-5">
								<p className="text-xs text-muted-foreground">
									{formatDate(post.publishedAt)}
								</p>
								<h2 className="text-base font-medium text-foreground transition-colors group-hover:text-primary">
									{post.title}
								</h2>
								{post.metaDescription ? (
									<p className="line-clamp-3 text-sm text-muted-foreground">
										{post.metaDescription}
									</p>
								) : null}
							</div>
						</Link>
					))}
				</div>
			)}
		</article>
	);
}
